import { find, findAll } from "./dom";

/**
 * Steps the phone's action bar out of the way while it would repeat what is on screen.
 *
 * <p>The bar is a fixed link to the contact page. When the page's own button to the same place is
 * in view — the booking card on an accommodation, the call to action on the wedding page — two
 * identical buttons stand in one screen, and when the footer is in view the bar covers the last
 * links on the page. The start page's hero counts too: it is a call to action of its own. In each
 * case the bar slides away, and it comes back once they have scrolled past.</p>
 *
 * <p>Progressive enhancement: without the script the bar simply stays, and the body's bottom
 * padding keeps it clear of the footer's last line.</p>
 */
export function initActionBar(): void {
  const bar = find<HTMLElement>("[data-action-bar]");
  const link = bar === null ? null : find<HTMLAnchorElement>("a[href]", bar);
  if (bar === null || link === null || !("IntersectionObserver" in window)) {
    return;
  }

  // Same page, any query: the accommodation's own button carries the room in ?boende=, the bar
  // too, but the list page's card-less buttons carry only ?arende=. Either way it is the same form.
  const target = link.pathname;
  const twins = findAll<HTMLAnchorElement>("main a[href]").filter((a) => a.pathname === target);
  // The start page's hero is its own call to action, four buttons and the rating: the bar waits
  // until it has scrolled away, rather than covering the rating on the first screen.
  const hero = find<HTMLElement>("main > .hero");
  const footer = find<HTMLElement>("footer.foot");
  const watched: Element[] = [...twins, ...[hero, footer].filter((element) => element !== null)];
  if (watched.length === 0) {
    return;
  }

  const inView = new Set<Element>();
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        inView.add(entry.target);
      } else {
        inView.delete(entry.target);
      }
    }
    bar.classList.toggle("action-bar--away", inView.size > 0);
    bar.classList.add("action-bar--live");
  });

  for (const element of watched) {
    observer.observe(element);
  }
}
